import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { T, useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import LinkButton from '@rewardopl/react-ui/link_button';

import { get, post } from '@rewardopl/utils/network';
import { stringify } from '@rewardopl/utils/url';

import { FormWrapper, Main, PageWrapper } from '../../../login/index.styles';
import { ButtonWrapper } from '../../../login/main/index.styles';

import Footer from '../../../../footer';
import Header from '../../../../header';
import Heading from '../../../../heading';
import LoadingText from '../../../../loading_text';

import {
  currentAccountRoleState,
  currentBusinessState,
  maybeCurrentAccountState,
} from '../../../../../store';

import type { Account, AccountRole, Business } from '@rewardopl/types';

function ContinueInApp() {
  const location = useLocation();

  return (
    <PageWrapper>
      <Header />
      <FormWrapper>
        <Main>
          <Heading align="center">Continue in app</Heading>
          <ButtonWrapper>
            <LinkButton
              as="a"
              className="primary"
              // @ts-expect-error - see https://github.com/styled-components/styled-components/issues/4112
              href={`rewardo-app://${location.pathname}${location.search}`}
            >
              <T>Continue in app</T>
            </LinkButton>
          </ButtonWrapper>
        </Main>
      </FormWrapper>
      <Footer />
    </PageWrapper>
  );
}

type AuthCallbackRedirectProps = {
  code: string;
  returnPath: string;
  state: string | null;
};

let didSend = false;

function AuthCallbackRedirect({ code, returnPath, state }: AuthCallbackRedirectProps) {
  const navigate = useNavigate();
  const setCurrentAccount = useSetAtom(maybeCurrentAccountState);
  const setCurrentAccountRole = useSetAtom(currentAccountRoleState);
  const setCurrentBusiness = useSetAtom(currentBusinessState);
  const loginErrorString = useTranslation('Failed to log in');

  const action1 = `/api/auth/google/callback${stringify({
    state: state ? encodeURIComponent(state) : '',
  })}`;
  const action2 = '/api/account_roles/current';
  const action3 = '/api/businesses/current';

  useEffect(() => {
    if (didSend) {
      return;
    }

    didSend = true;

    const validatedReturnPath =
      returnPath.startsWith('/') && !returnPath.startsWith('//') ? returnPath : '/';

    (async () => {
      try {
        const account = (await post(action1, {
          body: { code },
        })) as Account;

        const [accountRole, business] = await Promise.all([
          get(action2) as Promise<AccountRole>,
          account.business_id ? (get(action3) as Promise<Business>) : Promise.resolve(null),
        ]);

        setCurrentAccount(account);
        setCurrentAccountRole(accountRole);
        if (business) {
          setCurrentBusiness(business);
        }

        setTimeout(() => navigate(validatedReturnPath), 0);
      } catch {
        toast.error(loginErrorString);

        navigate('/login');
      }

      didSend = false;
    })();
  }, [
    action1,
    code,
    loginErrorString,
    navigate,
    returnPath,
    setCurrentAccount,
    setCurrentAccountRole,
    setCurrentBusiness,
  ]);

  return <LoadingText />;
}

export default function AuthLoginCallback() {
  const [searchParams] = useSearchParams();

  const code = searchParams.get('code');

  if (!code) {
    return <Navigate to="/login" />;
  }

  const state = searchParams.get('state');

  const decodedState = (state ? JSON.parse(decodeURIComponent(state)) : null) as {
    returnPath?: string;
    mobile?: boolean;
  } | null;
  const returnPath = decodedState?.returnPath || '/';
  const isMobileLogin = decodedState?.mobile;

  if (isMobileLogin) {
    return <ContinueInApp />;
  }

  return <AuthCallbackRedirect code={code} returnPath={returnPath} state={state} />;
}
